import { ViewerEngineEnum } from 'Data/enums/ViewerEngine';
import { BucketFileStatusEnum } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { IFileInfo } from './IGetFileInfo';

/**
 * Contrato do `GET /api/v2/viewer-artifacts` — o ÚNICO endpoint que devolve URL
 * assinada de artefato. O `GetFileInfo` traz só o status, para o poll.
 *
 * ⚠️ Casing: a API serializa com `DefaultContractResolver` (Newtonsoft), que
 * mantém os nomes do C#. Este endpoint devolve uma CLASSE (`PascalCase`); o
 * `GetFileInfo` e o `Render` montam `JObject` com chaves minúsculas. Por isso
 * as duas famílias de campo convivem aqui.
 */
export type ViewerEngineName = 'autodesk' | 'native';

/**
 * Urn da conversão nativa (`native:{sha256}`). Não é urn da Autodesk: mandar
 * para o Forge não abre nada. O backend a devolve no lugar do urn quando a
 * conversão mostrada é a nossa.
 */
export const NATIVE_URN_PREFIX = 'native:';

export const isNativeUrn = (urn?: string | null): boolean =>
  !!urn && urn.startsWith(NATIVE_URN_PREFIX);

/** A conversão MOSTRADA na linha da listagem é a nativa. */
export const isNativeRender = (file: {
  RenderEngine?: ViewerEngineEnum;
  Urn?: string | null;
}): boolean => file.RenderEngine === ViewerEngineEnum.Native || isNativeUrn(file.Urn);

/**
 * Linha da listagem. Um arquivo pode ter a conversão da Autodesk e a nativa ao
 * mesmo tempo; `AutodeskStatus`/`NativeStatus` dão cada uma. Backend anterior a
 * esses campos só tem a conversão mostrada (`RenderingStatus`).
 */
interface IListedConversions {
  RenderingStatus?: BucketFileStatusEnum | null;
  RenderEngine?: ViewerEngineEnum;
  Urn?: string | null;
  AutodeskStatus?: BucketFileStatusEnum | null;
  NativeStatus?: BucketFileStatusEnum | null;
}

const hasConversionLanes = (file: IListedConversions) => file.AutodeskStatus !== undefined;

export const nativeStatusOf = (file: IListedConversions): BucketFileStatusEnum | null => {
  if (hasConversionLanes(file)) { return file.NativeStatus ?? null; }
  return isNativeRender(file) ? file.RenderingStatus ?? null : null;
};

export const autodeskStatusOf = (file: IListedConversions): BucketFileStatusEnum => {
  if (hasConversionLanes(file)) { return file.AutodeskStatus ?? BucketFileStatusEnum.NotRendered; }
  return isNativeRender(file)
    ? BucketFileStatusEnum.NotRendered
    : file.RenderingStatus ?? BucketFileStatusEnum.NotRendered;
};

export const isNativeReady = (file: IListedConversions) =>
  nativeStatusOf(file) === BucketFileStatusEnum.Rendered;

/** Uma conversão por vez: com uma andando, o arquivo não pede a do outro motor. */
export const isConverting = (file: IListedConversions) =>
  nativeStatusOf(file) === BucketFileStatusEnum.Rendering
  || autodeskStatusOf(file) === BucketFileStatusEnum.Rendering;

/**
 * A conversão que a linha mostra, na mesma ordem do backend (`ViewerLanes.Present`):
 * a em andamento, depois a nativa pronta, depois a da Autodesk pronta.
 */
const presentConversion = (
  autodesk: BucketFileStatusEnum,
  native: BucketFileStatusEnum | null,
): { engine: ViewerEngineEnum; status: BucketFileStatusEnum } => {
  const shownNative = autodesk !== BucketFileStatusEnum.Rendering && (
    native === BucketFileStatusEnum.Rendering
    || native === BucketFileStatusEnum.Rendered
    || (autodesk !== BucketFileStatusEnum.Rendered && native === BucketFileStatusEnum.Error)
  );
  return shownNative
    ? { engine: ViewerEngineEnum.Native, status: native as BucketFileStatusEnum }
    : { engine: ViewerEngineEnum.Autodesk, status: autodesk };
};

/** Aplica o status novo na conversão de um motor e recalcula a mostrada. */
export const withConversionStatus = <T extends IListedConversions>(
  file: T,
  engine: ViewerEngineName,
  status: BucketFileStatusEnum,
): T => {
  const autodeskStatus = engine === 'autodesk' ? status : autodeskStatusOf(file);
  const nativeStatus = engine === 'native' ? status : nativeStatusOf(file);
  const shown = presentConversion(autodeskStatus, nativeStatus);
  return {
    ...file,
    AutodeskStatus: autodeskStatus,
    NativeStatus: nativeStatus,
    RenderingStatus: shown.status,
    RenderEngine: shown.engine,
  };
};

/**
 * A linha como o fluxo da Autodesk precisa ver: só a conversão dela. Sem isto,
 * um arquivo só com a nativa pronta passaria por convertido e abriria o Forge
 * sem urn.
 */
export const asAutodeskConversion = <T extends IListedConversions>(file: T): T => {
  const status = autodeskStatusOf(file);
  if (status === file.RenderingStatus && !isNativeRender(file)) { return file; }
  return {
    ...file,
    RenderingStatus: status,
    RenderEngine: ViewerEngineEnum.Autodesk,
    Urn: isNativeUrn(file.Urn) ? null : file.Urn,
  };
};

const AUTODESK_NOT_RENDERED = 'not-rendered';

// O backend já manda `urn`/`fileRenderStatus` nulos no arquivo não convertido;
// o tipo de `IFileInfo` é que nunca admitiu isso.
const NONE = null as unknown as string;

/**
 * O GetFileInfo como a tela da Autodesk precisa ver: `urn`, `fileRenderStatus`
 * e `isRendering` da conversão dela (os do backend mostram a nativa quando ela
 * está pronta). Não convertido sai com `urn`/`fileRenderStatus` nulos, que é o
 * que a tela e o reducer entendem por "pedir a conversão".
 */
export const asAutodeskFileInfo = (info: IFileInfo): IFileInfo => {
  if (!info) { return info; }
  if (info.autodeskStatus === undefined) {
    // Backend anterior: o urn `native:` é a única pista, e para o Forge o
    // arquivo não foi convertido.
    if (!isNativeUrn(info.urn) && !(info.viewerEngine === 'native' && !info.urn)) { return info; }
    return { ...info, urn: NONE, fileRenderStatus: NONE, isRendering: false };
  }
  const status = info.autodeskStatus === AUTODESK_NOT_RENDERED ? null : info.autodeskStatus;
  return {
    ...info,
    urn: info.autodeskUrn ?? NONE,
    fileRenderStatus: status ?? NONE,
    isRendering: status === 'rendering',
    viewerEngine: 'autodesk',
  };
};

export type NativeViewerStatus = 'parsing' | 'ready' | 'failed' | 'not_found';

/**
 * O backend roda 3 estágios, mas a barra de progresso é contínua entre eles:
 * o estágio serve só para rotular o que está acontecendo.
 */
export type NativeViewerStage = 'download' | 'parse' | 'artifacts';

/** Chaves do dicionário como o backend monta (`IfcArtifactStorageHelper.KeyFor`). */
export interface INativeViewerArtifactUrls {
  mesh: string;
  vertex: string;
  index: string;
  metadata: string;
  dataModel: string;
  symbolic: string;
}

/** `NativeViewerArtifactsViewModel` do backend — PascalCase. */
export interface INativeViewerArtifacts {
  Status: NativeViewerStatus;
  Engine?: ViewerEngineName | null;
  Stage?: NativeViewerStage | null;
  Progress: number;
  FailureCode?: string | null;
  /** Só com `Status === 'ready'`: SAS de 1 h, string-idênticas dentro da janela. */
  Artifacts?: INativeViewerArtifactUrls | null;
  ExpiresAt?: string | null;
}

/**
 * Resposta do render (`JObject`, chaves minúsculas). Com o nativo aceito vem
 * `engine: 'native'`; rebaixado pelo gate do backend vem a resposta Autodesk de
 * sempre, sem `engine` — e aí o job Autodesk JÁ foi disparado.
 */
export interface IRenderViewerResponse {
  urn: string | null;
  status: string;
  isRendering: boolean | number;
  fileIdentifier: string;
  engine?: ViewerEngineName | null;
  nativeJobId?: number | null;
}

export interface IRequestNativeViewerArtifacts {
  constructionSiteId: number;
  fileApiId: string;
}

/** Etapa mostrada ao usuário enquanto o backend prepara o modelo. */
export type NativeViewerProgressStep = 'queued' | 'download' | 'upload' | 'processing';

export interface INativeViewerProgress {
  step: NativeViewerProgressStep;
  /** 0–100 dentro da etapa, como o hub já manda para a Autodesk. */
  percent: number;
}

export type INativeViewerArtifactsReady = INativeViewerArtifacts & {
  Artifacts: INativeViewerArtifactUrls;
};

// Só exige o que desenha: `dataModel` e `symbolic` faltando não podem segurar a
// geometria — a árvore degrada para "indisponível" sozinha.
export const hasReadyNativeArtifacts = (
  value?: INativeViewerArtifacts | null,
): value is INativeViewerArtifactsReady => (
  value?.Status === 'ready'
  && !!value.Artifacts?.mesh
  && !!value.Artifacts.vertex
  && !!value.Artifacts.index
);
